import appConstants from '@appConstants';

export const UNAPPOINTED_PROS_ROUTE_MOVE_MESSAGES = {
  title: 'Moving selected shipments to the city route.',
  subtitle: 'Please wait while we assign the selected pros before starting optimization.'
};

export type ValidationStep = {
  id: string;
  label: string;
  message: string;
  data: Array<{ proId: string }>;
  cityRouteForUnappointedPros?: string;
};

export const getCityRouteForUnappointedProsFromSteps = (
  steps: ValidationStep[]
): string | undefined => {
  const unappointedProsStep = steps.find(
    (step) => step.id === appConstants.DYNAMIC_ROUTING_VALIDATION_STEPS.UNAPPOINTED_PROS
  );
  return unappointedProsStep?.cityRouteForUnappointedPros;
};

export const shouldMoveUnappointedProsBeforeOptimization = (
  selectedPros: string[],
  cityRouteForUnappointedPros?: string
): boolean => Boolean(cityRouteForUnappointedPros && selectedPros.length > 0);

export const buildUnappointedProsRouteAssignmentPayload = ({
  scenarioId,
  cityRouteForUnappointedPros,
  pros
}: {
  scenarioId: number;
  cityRouteForUnappointedPros: string;
  pros: string[];
}) => ({
  scenarioId,
  routeName: cityRouteForUnappointedPros,
  isTrapRoute: false,
  pros
});

export const buildValidationCompleteLogContext = ({
  reviewTime,
  validationStartTime,
  excludedProsList,
  prosToMoveToCityRoute,
  cityRouteForUnappointedPros,
  routeNames
}: {
  reviewTime: number;
  validationStartTime: number;
  excludedProsList: string[];
  prosToMoveToCityRoute: string[];
  cityRouteForUnappointedPros?: string;
  routeNames?: string[];
}) => {
  const shouldMovePros = shouldMoveUnappointedProsBeforeOptimization(
    prosToMoveToCityRoute,
    cityRouteForUnappointedPros
  );

  return {
    reviewTime,
    validationTime: Math.round((Date.now() - validationStartTime) / 1000),
    excludedProCount: excludedProsList?.length ?? 0,
    proMoveCount: prosToMoveToCityRoute?.length ?? 0,
    shouldMovePros,
    cityRouteName: shouldMovePros ? cityRouteForUnappointedPros : null,
    selectedRouteCount: routeNames?.length ?? 0
  };
};

type PostDataFn = (apiName: string, payload: Record<string, unknown>) => Promise<{ status?: number }>;

export const assignUnappointedProsToCityRoute = async ({
  scenarioId,
  cityRouteForUnappointedPros,
  pros,
  postData
}: {
  scenarioId: number;
  cityRouteForUnappointedPros: string;
  pros: string[];
  postData: PostDataFn;
}) => {
  const routeAssignmentPayload = buildUnappointedProsRouteAssignmentPayload({
    scenarioId,
    cityRouteForUnappointedPros,
    pros
  });

  const response = await postData('UpdateShipmentRouteAssignment', routeAssignmentPayload);

  if (!response || Math.trunc(response.status / 100) !== 2) {
    return { success: false as const, response, routeAssignmentPayload };
  }

  return { success: true as const, response, routeAssignmentPayload };
};
